import { GetRowIdParams } from "ag-grid-enterprise";
import { FeatureTypes } from "entity-app/constants";
import {
  EWorkflowStatusDataBlock,
  LINKED_ACTIVITIES_COLUMN_FILTER_LABELS
} from "../constants";

export const getRowId = (params: GetRowIdParams<{ id: string }>) =>
  params.data.id;

/**
 * Checks if a value is considered empty (null, undefined, empty string, or empty array)
 */
const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/**
 * Comparator function that puts empty values at the bottom when sorting.
 * Works with strings, numbers, dates, and other types.
 *
 * @param valueA - First value to compare
 * @param valueB - Second value to compare
 * @returns -1 if valueA < valueB, 0 if equal, 1 if valueA > valueB. Empty values are always sorted to the bottom.
 */
export const comparatorWithEmptyAtBottom = (
  valueA: any,
  valueB: any,
  _nodeA?: any,
  _nodeB?: any,
  isInverted?: boolean
): number => {
  const isEmptyA = isEmpty(valueA);
  const isEmptyB = isEmpty(valueB);

  // If both are empty, they are equal
  if (isEmptyA && isEmptyB) {
    return 0;
  }

  /**
   * ag-grid will invert sort order for desc. To keep empties ALWAYS at the bottom,
   * we adjust the sign for the "empty vs non-empty" cases using the `isInverted` flag.
   */
  const emptySortSign = isInverted ? -1 : 1;

  // If valueA is empty, it goes to bottom
  if (isEmptyA && !isEmptyB) {
    return 1 * emptySortSign;
  }

  // If valueB is empty, it goes to bottom
  if (!isEmptyA && isEmptyB) {
    return -1 * emptySortSign;
  }

  // Both values are non-empty, perform normal comparison
  // Handle different types
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }

  if (typeof valueA === "string" && typeof valueB === "string") {
    return valueA.localeCompare(valueB, undefined, {
      numeric: true,
      sensitivity: "base"
    });
  }

  // Fallback to string comparison
  const strA = String(valueA);
  const strB = String(valueB);
  return strA.localeCompare(strB, undefined, {
    numeric: true,
    sensitivity: "base"
  });
};

/** Severity rank: lower = more severe. ASC renders HIGH → MEDIUM → LOW. */
const RISK_LEVEL_RANK: Record<string, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
  DELETED: 3
};

/**
 * AG Grid comparator for Risk columns. Sorts by severity (HIGH → MEDIUM → LOW,
 * then DELETED) for ASC. Empty/unknown values always sort to the bottom for both
 * asc and desc (uses the v28+ isInverted flag, like comparatorWithEmptyAtBottom).
 * Case-insensitive on the raw risk string.
 */
export const riskLevelComparator = (
  valueA: any,
  valueB: any,
  _nodeA?: any,
  _nodeB?: any,
  isInverted?: boolean
): number => {
  const norm = (v: any): string =>
    typeof v === "string" ? v.trim().toUpperCase() : "";
  const rankA = RISK_LEVEL_RANK[norm(valueA)];
  const rankB = RISK_LEVEL_RANK[norm(valueB)];
  const hasA = rankA !== undefined;
  const hasB = rankB !== undefined;

  if (!hasA && !hasB) return 0;

  // Keep unknown/empty at the bottom for both asc and desc.
  const emptySortSign = isInverted ? -1 : 1;
  if (!hasA && hasB) return 1 * emptySortSign;
  if (hasA && !hasB) return -1 * emptySortSign;

  return rankA - rankB; // HIGH(0) < MEDIUM(1) < LOW(2) < DELETED(3)
};

const NOT_FOUND_IN_SOURCE_COL_ID = "not_found_in_source";

/** True if value means "Missing" (not found in source). */
const isMissingTag = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value === true;
  if (typeof value === "number") return value !== 0;
  const s = String(value).trim().toLowerCase();
  return s === "missing" || s === "true" || s === "1";
};

/**
 * Comparator for "not_found_in_source" (Present in latest sync):
 * Missing tagged rows always on top, rest (Present + empty) at bottom, for both asc and desc sort.
 * Relies on AG Grid's isInverted parameter (v28+); no API fallback to avoid O(n log n) getColumnState calls.
 */
export const notFoundInSourceComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number => {
  const rawA = valueA ?? nodeA?.data?.[NOT_FOUND_IN_SOURCE_COL_ID];
  const rawB = valueB ?? nodeB?.data?.[NOT_FOUND_IN_SOURCE_COL_ID];
  const aMissing = isMissingTag(rawA);
  const bMissing = isMissingTag(rawB);

  if (aMissing === bMissing) return 0;

  const sign = isInverted === true ? -1 : 1;

  // A Missing, B rest → A first (return negative)
  if (aMissing && !bMissing) return -1 * sign;
  // A rest, B Missing → B first (return positive)
  return 1 * sign;
};

/**
 * Converts an array of items to a comparable sort string (empty array → null).
 * Used by linked column comparators.
 */
export const linkedArraySortValue = (
  val: any,
  nameKey: (item: any) => string
): string | null => {
  if (Array.isArray(val) && val.length === 0) return null;
  if (!Array.isArray(val)) return val;
  const names = val
    .map(nameKey)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return names.join(", ");
};

/** Derives task/activity display name; matches coldefLinkedActivities valueGetter. */
// The trailing `?? ""` fallback below is unreachable: the (activity_id ? ... : task_name ?? "")
// ternary always yields a string, so the outer `??` never falls through to the literal "".
// v8-to-istanbul keys this branch off the `??` expression's start line, so the ignore must sit here.
/* v8 ignore next */
const linkedTaskNameKey = (t: any): string =>
  t?.name ??
  (t?.activity_id
    ? `${t.activity_id} - ${t.task_name ?? ""}`
    : (t?.task_name ?? ""));

/**
 * Sort key for linked tasks: use the *first* activity's name (display order) so
 * grid order matches the first chip users see. Empty array → null (sorts to bottom).
 */
const linkedTasksSortValue = (val: any): string | null => {
  if (!Array.isArray(val) || val.length === 0) return null;
  return linkedTaskNameKey(val[0]);
};

/**
 * AG Grid comparator for linked tasks/activities columns. Empty at bottom, then sort by task names.
 */
export const linkedTasksComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number =>
  comparatorWithEmptyAtBottom(
    linkedTasksSortValue(valueA),
    linkedTasksSortValue(valueB),
    nodeA,
    nodeB,
    isInverted
  );

const linkedMaterialNameKey = (m: any): string =>
  m?.name ?? m?.title ?? m?.sequence_id ?? "";

/**
 * Sort key for review/final response arrays: join response strings from items.
 * Supports submittal_response.response, response, and comment.
 */
const reviewResponsesSortValue = (val: any): string | null => {
  if (!Array.isArray(val) || val.length === 0) return null;
  const parts = val.map((item: any) => item?.response ?? "");
  return parts.filter(Boolean).join(" ");
};

/**
 * AG Grid comparator for review_response and final_response columns.
 */
export const reviewResponsesComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number =>
  comparatorWithEmptyAtBottom(
    reviewResponsesSortValue(valueA),
    reviewResponsesSortValue(valueB),
    nodeA,
    nodeB,
    isInverted
  );

/**
 * Sort key for linked materials: use the *first* item's name (display order) so
 * grid order matches the first chip users see. Empty array → null (sorts to bottom).
 */
const linkedMaterialsSortValue = (val: any): string | null => {
  if (!Array.isArray(val) || val.length === 0) return null;
  return linkedMaterialNameKey(val[0]);
};

/**
 * AG Grid comparator for linked materials columns. Empty at bottom, then sort by first material name/title.
 */
export const linkedMaterialsComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number =>
  comparatorWithEmptyAtBottom(
    linkedMaterialsSortValue(valueA),
    linkedMaterialsSortValue(valueB),
    nodeA,
    nodeB,
    isInverted
  );

/** Display name for submittal (matches cell renderer: sequence_id - name). */
const linkedSubmittalDisplayName = (s: any): string => {
  const id =
    s?.sequence_id ?? s?.submittal_sequence_id ?? s?.submittal_id ?? "";
  const name = s?.name ?? s?.title ?? "";
  return id ? `${id} - ${name}`.trim() : name || "";
};

/**
 * Same order as SubmittalLinksEntityCellRenderer: Void first, then risk (High > Medium > Low), then sequence_id.
 */
const sortSubmittalsLikeCellRenderer = (items: any[]): any[] => {
  const riskOrder: Record<string, number> = {
    High: 3,
    Medium: 2,
    Low: 1
  };
  return [...items].sort((a, b) => {
    const aVoid = a?.workflow_status === EWorkflowStatusDataBlock.VOID;
    const bVoid = b?.workflow_status === EWorkflowStatusDataBlock.VOID;
    if (aVoid && !bVoid) return -1;
    if (!aVoid && bVoid) return 1;
    const riskA = riskOrder[a?.risk_level ?? ""] ?? 0;
    const riskB = riskOrder[b?.risk_level ?? ""] ?? 0;
    if (riskB !== riskA) return riskB - riskA;
    return (a?.sequence_id ?? 0) - (b?.sequence_id ?? 0);
  });
};

/**
 * Sort key for linked submittals: use the *first* submittal in the same order as the cell
 * (Void first, then risk, then sequence_id) so grid sort matches the first chip. Empty → null.
 */
const linkedSubmittalsSortValue = (val: any): string | null => {
  const items = Array.isArray(val)
    ? val.filter((x: any) => x?.feature_type === FeatureTypes.SUBMITTALS)
    : [];
  if (items.length === 0) return null;
  const sorted = sortSubmittalsLikeCellRenderer(items);
  return linkedSubmittalDisplayName(sorted[0]);
};

/**
 * AG Grid comparator for linked submittals columns (e.g. when value is linked_items filtered by submittals). Empty at bottom, then sort by submittal names/titles.
 */
export const linkedSubmittalsComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number =>
  comparatorWithEmptyAtBottom(
    linkedSubmittalsSortValue(valueA),
    linkedSubmittalsSortValue(valueB),
    nodeA,
    nodeB,
    isInverted
  );

const firstArrayItemSortValue = (
  val: any,
  nameKey: (item: any) => string
): string | null => {
  if (!Array.isArray(val) || val.length === 0) return null;
  const name = nameKey(val[0]);
  return name?.trim?.() ? name : null;
};

const locationNameKey = (loc: any): string => loc?.name ?? loc?.path ?? "";

/**
 * Comparator for `locations` (LBS) column:
 * - Empty locations sort to bottom
 * - Otherwise sorts by the first displayed location chip (display order)
 */
export const linkedLocationsComparator = (
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  isInverted?: boolean
): number =>
  comparatorWithEmptyAtBottom(
    firstArrayItemSortValue(valueA, locationNameKey),
    firstArrayItemSortValue(valueB, locationNameKey),
    nodeA,
    nodeB,
    isInverted
  );

export const backwardCompatibilityForLinkedTasksFilter = (
  sortedColumnConfig: any
) => {
  const linkedTasksColumnConfig = sortedColumnConfig.find(
    (col: any) => col.field.name === "linked_tasks"
  );

  const agFilter = linkedTasksColumnConfig?.field?.agFilter;
  if (agFilter) {
    const parsedFilter = JSON.parse(agFilter);
    if (
      parsedFilter.values.length === 1 &&
      parsedFilter.values[0] ===
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.ACTIVITY_LINKED
    ) {
      parsedFilter.values = [
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.CRITICAL,
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.NOT_CRITICAL
      ];
    }
    linkedTasksColumnConfig.field.agFilter = JSON.stringify(parsedFilter);
  }
  return sortedColumnConfig;
};

const getPrimaryGroupSourceColId = (api: any): string | null => {
  const sourceGroupColIds = (api?.getRowGroupColumns?.() ?? [])
    .map((c: any) => c?.getColId?.() ?? c?.colId)
    .filter(Boolean) as string[];
  if (!sourceGroupColIds.length) return null;
  return sourceGroupColIds[sourceGroupColIds.length - 1];
};

const getAutoGroupColId = (columnState: any[]): string | undefined =>
  columnState.find((col: any) => col?.colId === "group_column")?.colId ||
  columnState.find((col: any) =>
    String(col?.colId || "").startsWith("ag-Grid-AutoColumn")
  )?.colId;

export const applyGroupedColumnAsPrimarySort = (
  api: any,
  options?: { clearSortForInactiveGroupColIds?: string[] }
) => {
  if (!api) return;
  const columnState = api.getColumnState?.() ?? [];
  const primarySourceColId = getPrimaryGroupSourceColId(api);
  if (!primarySourceColId) return;
  const clearSortForInactiveGroupColIds =
    options?.clearSortForInactiveGroupColIds ?? [];
  const inactiveGroupColIdsToClear = clearSortForInactiveGroupColIds.filter(
    (colId: string) => colId && colId !== primarySourceColId
  );

  const sourceColumnState = columnState.find(
    (col: any) => col?.colId === primarySourceColId
  );
  const primarySort = sourceColumnState?.sort || "asc";
  const autoGroupColId = getAutoGroupColId(columnState);

  const sortedColumns = columnState.filter((col: any) => Boolean(col?.sort));
  const currentSortedSecondary = sortedColumns
    .filter((col: any) => col?.colId !== primarySourceColId)
    .filter(
      (col: any) => !inactiveGroupColIdsToClear.includes(String(col?.colId))
    )
    .filter((col: any) => col?.colId !== autoGroupColId)
    .filter(
      (col: any) => !String(col?.colId || "").startsWith("ag-Grid-AutoColumn")
    )
    .sort((a: any, b: any) => {
      const aIdx =
        typeof a?.sortIndex === "number"
          ? a.sortIndex
          : Number.MAX_SAFE_INTEGER;
      const bIdx =
        typeof b?.sortIndex === "number"
          ? b.sortIndex
          : Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    });

  api.applyColumnState?.({
    state: [
      {
        colId: primarySourceColId,
        sort: primarySort,
        sortIndex: 0
      },
      ...inactiveGroupColIdsToClear.map((colId: string) => ({
        colId,
        sort: null,
        sortIndex: null
      })),
      ...currentSortedSecondary.map((col: any, index: number) => ({
        colId: col.colId,
        sort: col.sort,
        sortIndex: index + 1
      }))
    ],
    applyOrder: false
  });
  api.refreshHeader?.();
};

export const enforceSingleRowGroupSelection = (params: {
  api?: any;
  fallbackApi?: any;
  clearSortForInactiveGroupColIds?: string[];
}) => {
  const api = params?.api ?? params?.fallbackApi;
  if (!api) return;

  const rowGroupColumns = api.getRowGroupColumns?.() ?? [];
  if (!rowGroupColumns?.length) {
    const colIdsToClear = params?.clearSortForInactiveGroupColIds ?? [];
    if (!colIdsToClear.length) return;

    api.applyColumnState?.({
      state: colIdsToClear.map((colId: string) => ({
        colId,
        sort: null,
        sortIndex: null
      })),
      applyOrder: false
    });
    api.refreshHeader?.();
    return;
  }

  if (rowGroupColumns.length <= 1) {
    const singleGroupCol = rowGroupColumns[0];
    const singleGroupColId =
      singleGroupCol?.getColId?.() ?? singleGroupCol?.colId;
    if (singleGroupColId) {
      setTimeout(() => {
        applyGroupedColumnAsPrimarySort(api, {
          clearSortForInactiveGroupColIds:
            params?.clearSortForInactiveGroupColIds
        });
      }, 0);
    }
    return;
  }

  const lastGroupCol = rowGroupColumns[rowGroupColumns.length - 1];
  const lastGroupColId = lastGroupCol?.getColId?.() ?? lastGroupCol?.colId;
  if (!lastGroupColId) return;

  api.setRowGroupColumns([lastGroupColId]);
  setTimeout(() => {
    applyGroupedColumnAsPrimarySort(api, {
      clearSortForInactiveGroupColIds: params?.clearSortForInactiveGroupColIds
    });
  }, 0);
};

export const getGroupRoute = (node: any): string => {
  const segments: string[] = [];
  let current = node;
  while (current?.group) {
    const groupColId =
      current?.rowGroupColumn?.getColId?.() ?? current?.field ?? "";
    const groupKey = String(current?.key ?? "");
    segments.push(`${groupColId}:${groupKey}`);
    current = current?.parent;
  }
  return segments.reverse().join("|");
};

export const captureExpandedGroupRoutes = (api: any): Set<string> => {
  if (!api?.forEachNode) return new Set();
  const routes = new Set<string>();
  api.forEachNode((node: any) => {
    if (!node?.group || !node?.expanded) return;
    const route = getGroupRoute(node);
    // unreachable: node.group is truthy here, so getGroupRoute always yields a non-empty route
    /* v8 ignore next */
    if (route) routes.add(route);
  });
  return routes;
};

export const restoreExpandedGroupRoutes = (api: any, routes: Set<string>) => {
  if (!api?.forEachNode || !routes?.size) return;
  api.forEachNode((node: any) => {
    if (!node?.group) return;
    const route = getGroupRoute(node);
    if (route && routes.has(route)) {
      node.setExpanded(true);
    }
  });
};

export const isGroupOpenByDefaultFromRoutes = (
  params: any,
  routes: Set<string>
): boolean => {
  if (!params?.rowNode?.group) return false;
  const route = getGroupRoute(params.rowNode);
  // unreachable: rowNode.group is truthy here, so getGroupRoute always yields a non-empty route
  /* v8 ignore next */
  if (!route) return false;
  return routes.has(route);
};
